/**
 * Configuración de navegación — Define rutas, iconos y permisos del sidebar.
 * Exporta: NavChild, NavItem, NAV_ITEMS
 *
 * Los roles, claves de permiso y plan requerido se derivan de
 * `@/router/routeCatalog` (única fuente de verdad). Aquí solo vive la
 * estructura visual del menú: labels, iconos, agrupación y exclusiones de
 * visibilidad de menú (excludeRoles / requiresBuzonEquipo).
 */
import {
  LayoutDashboard,
  Wallet,
  Package,
  Users,
  FileText,
  BarChart3,
  Brain,
  CheckSquare,
  Clock,
  DollarSign,
  ShieldCheck,
  Store,
  Star,
  MessageSquareQuote,
  UtensilsCrossed,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Permission } from "@/types";
import type { PlanType } from "@/config/plans/planAccess";
import { getAppRoute } from "@/router/routeCatalog";
import { OWNER_ADMIN_ROLES, Role } from "@/config/roles";

export interface NavChild {
  label: string;
  path: string;
  roles: Role[];
  permissionKey?: keyof Permission;
  /** Roles excluidos explícitamente del MENÚ (no del acceso a la URL). */
  excludeRoles?: Role[];
}

export interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  roles: Role[];
  permissionKey?: keyof Permission;
  planRequired?: PlanType;
  children?: NavChild[];
  /** Roles excluidos explícitamente del MENÚ (no del acceso a la URL). */
  excludeRoles?: Role[];
  /** Solo visible si el buzón está activado (menú empleados). */
  requiresBuzonEquipo?: boolean;
}

// ── Lookups contra el catálogo de rutas (única fuente de permisos) ──────────
const routeRoles = (path: string): Role[] => getAppRoute(path)?.roles ?? [];
const routeModule = (path: string): keyof Permission | undefined =>
  getAppRoute(path)?.module;
const routePlan = (path: string): PlanType | undefined =>
  getAppRoute(path)?.plan;

/**
 * Configuración centralizada del menú de navegación.
 * Solo se edita aquí para cambiar labels, iconos o la estructura del sidebar;
 * los permisos se cambian en `router/routeCatalog.ts`.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/dashboard",
    roles: routeRoles("/dashboard"),
    permissionKey: routeModule("/dashboard"),
    planRequired: routePlan("/dashboard"),
  },
  {
    icon: Wallet,
    label: "Caja",
    path: "/caja",
    roles: routeRoles("/caja"),
    permissionKey: routeModule("/caja"),
    planRequired: routePlan("/caja"),
    children: [
      {
        label: "Ventas Individuales",
        path: "/caja/ventas",
        roles: routeRoles("/caja/ventas"),
        permissionKey: routeModule("/caja/ventas"),
      },
      {
        label: "Historial de Cierres",
        path: "/caja/cierres",
        roles: routeRoles("/caja/cierres"),
        permissionKey: routeModule("/caja/cierres"),
      },
      {
        label: "Movimientos de Caja",
        path: "/caja/movimientos",
        roles: routeRoles("/caja/movimientos"),
        permissionKey: routeModule("/caja/movimientos"),
      },
      {
        label: "Créditos",
        path: "/caja/creditos",
        roles: routeRoles("/caja/creditos"),
        permissionKey: routeModule("/caja/creditos"),
      },
      {
        label: "Fidelización",
        path: "/caja/fidelizacion",
        roles: routeRoles("/caja/fidelizacion"),
        permissionKey: routeModule("/caja/fidelizacion"),
      },
    ],
  },
  {
    icon: Package,
    label: "Inventario",
    path: "/inventario",
    roles: routeRoles("/inventario"),
    permissionKey: routeModule("/inventario"),
    planRequired: routePlan("/inventario"),
    children: [
      {
        label: "Combos Promocionales",
        path: "/inventario/promociones",
        roles: routeRoles("/inventario/promociones"),
        permissionKey: routeModule("/inventario/promociones"),
      },
    ],
  },
  {
    icon: Users,
    label: "Personal",
    path: "/personal",
    roles: routeRoles("/personal"),
    permissionKey: routeModule("/personal"),
    planRequired: routePlan("/personal"),
    children: [
      {
        label: "Tareas y Actividades",
        path: "/personal/tareas",
        roles: routeRoles("/personal/tareas"),
        permissionKey: routeModule("/personal/tareas"),
      },
      {
        label: "Asistencia",
        path: "/personal/asistencia",
        roles: routeRoles("/personal/asistencia"),
        permissionKey: routeModule("/personal/asistencia"),
      },
      {
        label: "Gestión de Nómina",
        path: "/personal/nomina",
        roles: routeRoles("/personal/nomina"),
        permissionKey: routeModule("/personal/nomina"),
      },
      {
        label: "Calendario de Turnos",
        path: "/personal/calendario",
        roles: routeRoles("/personal/calendario"),
        permissionKey: routeModule("/personal/calendario"),
      },
      {
        label: "Vacaciones",
        path: "/personal/vacaciones",
        roles: routeRoles("/personal/vacaciones"),
      },
      {
        label: "Vacantes",
        path: "/personal/vacantes",
        roles: routeRoles("/personal/vacantes"),
        permissionKey: routeModule("/personal/vacantes"),
      },
      {
        label: "Buzón del equipo",
        path: "/buzon-equipo",
        // En el submenú de Personal solo lo ven los gestores; los empleados
        // usan la entrada independiente "Buzón del equipo" de más abajo.
        roles: OWNER_ADMIN_ROLES,
        permissionKey: routeModule("/buzon-equipo"),
      },
    ],
  },
  {
    icon: ShieldCheck,
    label: "Higiene",
    path: "/higiene",
    roles: routeRoles("/higiene"),
    permissionKey: routeModule("/higiene"),
    planRequired: routePlan("/higiene"),
  },
  {
    icon: CheckSquare,
    label: "Mis Tareas",
    path: "/mis-tareas",
    roles: routeRoles("/mis-tareas"),
    excludeRoles: OWNER_ADMIN_ROLES,
  },
  {
    icon: Clock,
    label: "Mi Asistencia",
    path: "/mi-asistencia",
    roles: routeRoles("/mi-asistencia"),
    excludeRoles: OWNER_ADMIN_ROLES,
  },
  {
    icon: DollarSign,
    label: "Mi Nómina",
    path: "/mi-nomina",
    roles: routeRoles("/mi-nomina"),
    excludeRoles: OWNER_ADMIN_ROLES,
  },
  {
    icon: FileText,
    label: "Mi Perfil",
    path: "/mi-perfil",
    roles: routeRoles("/mi-perfil"),
    excludeRoles: OWNER_ADMIN_ROLES,
  },
  {
    icon: MessageSquareQuote,
    label: "Buzón del equipo",
    path: "/buzon-equipo",
    roles: routeRoles("/buzon-equipo"),
    excludeRoles: OWNER_ADMIN_ROLES,
    requiresBuzonEquipo: true,
  },
  {
    icon: FileText,
    label: "Auditoría",
    path: "/auditoria",
    roles: routeRoles("/auditoria"),
    permissionKey: routeModule("/auditoria"),
    planRequired: routePlan("/auditoria"),
  },
  {
    icon: BarChart3,
    label: "Métricas",
    path: "/metricas",
    roles: routeRoles("/metricas"),
    permissionKey: routeModule("/metricas"),
    planRequired: routePlan("/metricas"),
  },
  {
    icon: Brain,
    label: "IA Insights",
    path: "/ia",
    roles: routeRoles("/ia"),
    planRequired: routePlan("/ia"),
    permissionKey: routeModule("/ia"),
  },
  {
    icon: Star,
    label: "Calificaciones",
    path: "/calificaciones",
    roles: routeRoles("/calificaciones"),
    permissionKey: routeModule("/calificaciones"),
    planRequired: routePlan("/calificaciones"),
  },
  {
    icon: UtensilsCrossed,
    label: "Mesas",
    path: "/mesas",
    roles: routeRoles("/mesas"),
    permissionKey: routeModule("/mesas"),
    planRequired: routePlan("/mesas"),
    children: [
      {
        label: "Tomar Orden",
        path: "/mesas",
        roles: routeRoles("/mesas"),
        permissionKey: routeModule("/mesas"),
      },
      {
        label: "Órdenes",
        path: "/mesas/ordenes",
        roles: routeRoles("/mesas/ordenes"),
        permissionKey: routeModule("/mesas/ordenes"),
      },
      {
        label: "Cocina",
        path: "/mesas/cocina",
        roles: routeRoles("/mesas/cocina"),
        permissionKey: routeModule("/mesas/cocina"),
      },
      {
        label: "Mesas",
        path: "/mesas/configuracion",
        roles: routeRoles("/mesas/configuracion"),
        permissionKey: routeModule("/mesas/configuracion"),
      },
    ],
  },
  {
    icon: Store,
    label: "Tienda Virtual",
    path: "/tienda/pedidos",
    roles: routeRoles("/tienda/pedidos"),
    permissionKey: routeModule("/tienda/pedidos"),
    planRequired: routePlan("/tienda/pedidos"),
    children: [
      {
        label: "Liquidaciones de Domiciliarios",
        path: "/tienda/liquidaciones",
        roles: routeRoles("/tienda/liquidaciones"),
        permissionKey: routeModule("/tienda/liquidaciones"),
      },
      {
        label: "Configuración",
        path: "/tienda/configuracion",
        roles: routeRoles("/tienda/configuracion"),
        permissionKey: routeModule("/tienda/configuracion"),
      },
      {
        label: "Asistente de la tienda",
        path: "/tienda/configuracion/asistente",
        roles: routeRoles("/tienda/configuracion/asistente"),
        permissionKey: routeModule("/tienda/configuracion/asistente"),
      },
    ],
  },
];
